import { Typography } from '@mui/material';
import { Analytics } from '@watershed/analytics/analyticsUtils';
import CircleRemoveIcon from '@watershed/icons/components/CircleRemove';
import WarningIcon from '@watershed/icons/components/Warning';
import SyncIcon from '@watershed/icons/components/Sync';
import { maybeNotifySentry } from '@watershed/shared-frontend/utils/errorUtils';
import { useEffect } from 'react';
import PageContainer from '@watershed/shared-frontend/components/PageContainer';
import { ForbiddenPageBody } from '../components/ForbiddenPage';
import { NotFoundPageBody } from '../components/NotFoundPage';
import Button from '@watershed/ui-core/components/Button';
import { useUserContext } from '../utils/UserContext';
import { UnexpectedErrorState } from '@watershed/ui-core/components/UnexpectedErrorState';
import TextLink from '@watershed/ui-core/components/TextLink';

export interface FlexiError extends Error {
  code?: string;
}
function UnexpectedErrorPageBody({ error }: { error: FlexiError }) {
  useEffect(() => {
    maybeNotifySentry(error);
  }, [error]);
  return <UnexpectedErrorState />;
}

function CrossOrgErrorPageBody({ error }: { error: FlexiError }) {
  const user = useUserContext();

  useEffect(() => {
    if (
      !user.loginAsUser &&
      !user.userIsWatershedContractor &&
      !user.userIsWatershedEmployee
    )
      maybeNotifySentry(error);
  }, [error, user]);

  return (
    <>
      <CircleRemoveIcon size={128} marginBottom={1} color="warning.main" />
      <Typography variant="h1" gutterBottom>
        Cannot load across orgs while authenticated
      </Typography>
      <Typography variant="body2">Is this a stale tab?</Typography>
    </>
  );
}

function LogoutErrorPageBody() {
  return (
    <>
      <WarningIcon size={128} marginBottom={1} color="secondary.main" />
      <Typography variant="h1" gutterBottom>
        Your login session has expired
      </Typography>
      <Typography variant="body2">Redirecting you to login…</Typography>
    </>
  );
}

function GraphqlSchemaErrorPageBody({ error }: { error: FlexiError }) {
  useEffect(() => {
    maybeNotifySentry(error);
  }, [error]);

  return (
    <>
      <SyncIcon size={128} marginBottom={3} color="warning.main" />
      <Typography variant="h1" gutterBottom sx={{ lineHeight: 1.2 }}>
        Uh oh! Our application has gotten out of sync with your web browser.
      </Typography>
      <Typography variant="body2" sx={{ fontSize: 17 }} paragraph>
        Please reload the page to fix the issue.
      </Typography>
      <Button startIcon={<SyncIcon />} onClick={() => window.location.reload()}>
        Reload this page
      </Button>
    </>
  );
}

function DataResponseTooLargeErrorBody({ error }: { error: FlexiError }) {
  return (
    <>
      {' '}
      <WarningIcon size={128} marginBottom={1} color="error.main" />
      <Typography variant="h1" gutterBottom>
        The size of the data requested is too large
      </Typography>
      <Typography variant="body2">
        Try narrowing your query or contact support for assistance:{' '}
        <TextLink href="mailto:support@watershed.com">
          support@watershed.com
        </TextLink>
      </Typography>
    </>
  );
}

/**
 * Map of error codes to custom error pages. If an error does not have a code
 * with a custom page, the fallback "Unexpected error" is rendered.
 */
const errorPageBodyForCode: Record<
  string,
  (...args: Array<any>) => JSX.Element
> = {
  CROSS_ORG: CrossOrgErrorPageBody,
  UNAUTHENTICATED: LogoutErrorPageBody,
  FORBIDDEN: ForbiddenPageBody,
  NOT_FOUND: NotFoundPageBody,
  GRAPHQL_VALIDATION_FAILED: GraphqlSchemaErrorPageBody,
  DATA_RESPONSE_TOO_LARGE: DataResponseTooLargeErrorBody,
};

function getErrorPageForCode(error: FlexiError): JSX.Element {
  const ErrorPageBody =
    (error.code ? errorPageBodyForCode[error.code] : null) ??
    UnexpectedErrorPageBody;
  return <ErrorPageBody error={error} />;
}

export default function ErrorPage({ error }: { error: FlexiError }) {
  useEffect(() => {
    console.error(error);
    Analytics.error('errorPageShown', { code: error.code });
  }, [error]);

  return (
    <PageContainer
      isFullPage={false}
      maxWidth="sm"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        paddingTop: 64,
        paddingBottom: 64,
      }}
    >
      {getErrorPageForCode(error)}
    </PageContainer>
  );
}
